import { Card, Flex, JsonViewer, SimpleTable, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";

const { Title, Text } = Typography;

const JsonViewerPage = () => {
  const exampleJson = {
    block_number: 38733960,
    topic: "raw-avalanchec-mainnet",
    chain: "avalanchec-mainnet",
    block: {
      baseFeePerGas: "0x5f9ed8fd3",
      difficulty: "0x1",
      gasLimit: "0xe4e1c0",
      gasUsed: "0x46f0b1",
      hash: "0x42a9043d4a8b0d57a37dba1ec579aadca2bdeea44ec9383c05aae8d4b534abbc",
      miner: "0x0100000000000000000000000000000000000000",
      nonce: "0x0000000000000000",
      number: "0x24f0888",
      parentHash:
        "0x7a3654a7b66a5244c5993af3f8d49fb3c3ce68964485bbd589af6d9fa4ff33c1",
      size: "0x5ba6",
      timestamp: "0x6571aeb3",
      transactions: [
        {
          baseFeePerGas: "0x5f9ed8fd3",
          difficulty: "0x1",
          gasLimit: "0xe4e1c0",
          gasUsed: "0x46f0b1",
          hash: "0x42a9043d4a8b0d57a37dba1ec579aadca2bdeea44ec9383c05aae8d4b534abbc",
          miner: "0x0100000000000000000000000000000000000000",
          nonce: "0x0000000000000000",
          number: "0x24f0888",
          parentHash:
            "0x7a3654a7b66a5244c5993af3f8d49fb3c3ce68964485bbd589af6d9fa4ff33c1",
          size: "0x5ba6",
          timestamp: "0x6571aeb3",
          transactions: [],
        },
      ],
    },
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>JsonViewer</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The JsonViewer component provides a simple and customizable
            interface for displaying JSON objects in a readable format. It
            adapts to the current theme (light or dark) managed by the Djuno
            Design system, making it visually consistent with the rest of your
            application. The component leverages the JsonView library and allows
            you to collapse sections of the JSON data for easier navigation,
            particularly for large datasets.
          </Text>

          <Title level={5}>Key Features</Title>
          <Text size="sm"></Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                Supports light and dark themes based on the current app's mode.
              </Text>
            </li>
            <li>
              <Text size="sm">Allows collapsing of nested JSON objects.</Text>
            </li>
            <li>
              <Text size="sm">
                Automatically shortens long strings for better readability.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              codeViewerProps={{
                code: JsonViewerExample1,
              }}
            >
              <JsonViewer value={exampleJson} />
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* value */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">value</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The JSON object to display. If no value is provided, an
                        empty object {} will be rendered.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code className="whitespace-nowrap">
                        {`object | null`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`{}`}</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* collapsed */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">collapsed</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls how deeply nested JSON objects are collapsed.
                        true collapses everything, false expands everything.
                        Providing a number collapses objects up to that depth.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code className="whitespace-nowrap">
                        {`number | boolean`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">2</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* style */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">style</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Inline styles to apply to the JSON viewer container for
                        additional customization.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code className="whitespace-nowrap">
                        {`React.CSSProperties`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* copyable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">copyable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Enables copy functionality for JSON values.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code className="whitespace-nowrap">
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const JsonViewerExample1 = ` const exampleJson = {
block_number: 38733960,
topic: "raw-avalanchec-mainnet",
chain: "avalanchec-mainnet",
block: {
  baseFeePerGas: "0x5f9ed8fd3",
  difficulty: "0x1",
  gasLimit: "0xe4e1c0",
  gasUsed: "0x46f0b1",
  hash: "0x42a9043d4a8b0d57a37dba1ec579aadca2bdeea44ec9383c05aae8d4b534abbc",
  miner: "0x0100000000000000000000000000000000000000",
  nonce: "0x0000000000000000",
  number: "0x24f0888",
  parentHash:
    "0x7a3654a7b66a5244c5993af3f8d49fb3c3ce68964485bbd589af6d9fa4ff33c1",
  size: "0x5ba6",
  timestamp: "0x6571aeb3",
  transactions: [
    {
      baseFeePerGas: "0x5f9ed8fd3",
      difficulty: "0x1",
      gasLimit: "0xe4e1c0",
      gasUsed: "0x46f0b1",
      hash: "0x42a9043d4a8b0d57a37dba1ec579aadca2bdeea44ec9383c05aae8d4b534abbc",
      miner: "0x0100000000000000000000000000000000000000",
      nonce: "0x0000000000000000",
      number: "0x24f0888",
      parentHash:
        "0x7a3654a7b66a5244c5993af3f8d49fb3c3ce68964485bbd589af6d9fa4ff33c1",
      size: "0x5ba6",
      timestamp: "0x6571aeb3",
      transactions: [],
    },
  ],
},
};
<JsonViewer value={exampleJson} />
`;

export default JsonViewerPage;
